"use client"

import { useCallback, useEffect, useRef } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { PSEUDO_ID_COOKIE } from "@/platform/api/constants"
import { experimentsGateway } from "@/platform/api/gateways/experiments"
import { referralsGateway } from "@/platform/api/gateways/referrals"
import type { ReferralMeResponse, ReferralShare } from "@/platform/api/referrals"
import { isReferralNotTargeted, REFERRAL_ME_KEY } from "@/platform/api/referrals"
import { getSessionCookie } from "@/platform/session/auth/get-session-cookie-client"
import { usePrivateKey } from "@/platform/session/private-keys"
import { useGateway } from "@/platform/session/use-api"
import { useAuthenticatedViewer } from "@/platform/session/use-authenticated-viewer"
import { usePrivateMutation } from "@/platform/session/use-private-mutation"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useSession } from "@/platform/session/use-session"

export const useReferralMe = () => {
  const session = useSession()
  const referrals = useGateway(referralsGateway)
  const query = usePrivateQuery<ReferralMeResponse | null>({
    queryKey: REFERRAL_ME_KEY,
    queryFn: async () => {
      try {
        return await referrals.me()
      } catch (error) {
        if (isReferralNotTargeted(error)) return null
        throw error
      }
    },
    enabled: session.isAuthenticated,
    staleTime: 30_000,
    retry: false,
  })
  return { ...query, session, notTargeted: query.data === null, data: session.isAuthenticated ? (query.data ?? undefined) : undefined }
}

export const useMarketplaceReferralsEnabled = (): boolean => {
  const me = useReferralMe()
  const experiments = useGateway(experimentsGateway)
  const release = useQuery({
    queryKey: ["experiments", "marketplace_referrals_enabled"],
    queryFn: () => {
      const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE)
      return experiments.treatment("marketplace_referrals_enabled", { pseudoId: pseudoId || undefined, attributes: { pseudoId: pseudoId || "none" } })
    },
    enabled: me.session.status.kind === "anonymous",
    staleTime: 60_000,
    retry: false,
  })

  if (!me.session.isAuthenticated) return me.session.status.kind === "anonymous" && release.data?.config === true
  if (me.notTargeted) return false
  return me.data !== undefined || me.errorUpdatedAt > 0
}

const useGetOrCreateReferralShare = () => {
  const queryClient = useQueryClient()
  const referrals = useGateway(referralsGateway)
  const meKey = usePrivateKey(...REFERRAL_ME_KEY)

  return usePrivateMutation<ReferralShare, Error, void>({
    onMutate: () => queryClient.cancelQueries({ queryKey: meKey }),
    mutationFn: () => referrals.getOrCreateCode(),
    onSuccess: (share) => {
      queryClient.setQueryData<ReferralMeResponse | null>(meKey, (current) => (current ? { ...current, share } : current))
    },
  })
}

export const useReferralCodeOffer = (code: string | null) => {
  const referrals = useGateway(referralsGateway)
  return useQuery({
    queryKey: ["referrals", "code", code],
    queryFn: () => referrals.codeOffer(code ?? ""),
    enabled: code !== null,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export type ReferralOfferState =
  | { status: "loading" }
  | { status: "hidden" }
  | { status: "ready"; me: ReferralMeResponse; share: ReferralShare | null; creating: boolean; creationError: Error | null; ensureShare: () => Promise<ReferralShare> }

export const useReferralOffer = (): ReferralOfferState => {
  const me = useReferralMe()
  const { mutateAsync, isPending, error } = useGetOrCreateReferralShare()
  const existing = me.data?.share ?? null
  const ensureShare = useCallback(async () => existing ?? (await mutateAsync()), [existing, mutateAsync])

  if (!me.session.isAuthenticated || me.notTargeted || me.isError) return { status: "hidden" }
  if (!me.data) return { status: "loading" }
  return { status: "ready", me: me.data, share: existing, creating: isPending, creationError: error, ensureShare }
}

export type ReferralInviteState =
  | { status: "loading" }
  | { status: "hidden" }
  | { status: "unavailable"; retry: () => void }
  | { status: "ready"; me: ReferralMeResponse; share: ReferralShare }

export const useReferralInvite = (): ReferralInviteState => {
  const me = useReferralMe()
  const create = useGetOrCreateReferralShare()
  const viewer = useAuthenticatedViewer()
  const requested = useRef<string | null>(null)
  const needsShare = me.data !== undefined && !me.data.share
  const { mutate, reset, isIdle } = create
  const { refetch: refetchMe } = me
  const { isAuthenticated } = me.session

  useEffect(() => {
    if (!isAuthenticated || viewer === null) {
      requested.current = null
      return
    }
    if (!needsShare || requested.current === viewer) return
    requested.current = viewer
    mutate()
  }, [isAuthenticated, needsShare, mutate, isIdle, viewer])

  const retry = useCallback(() => {
    requested.current = null
    reset()
    void refetchMe()
  }, [reset, refetchMe])

  if (!isAuthenticated) return { status: "hidden" }
  if (me.notTargeted) return { status: "hidden" }
  if (create.isError && isReferralNotTargeted(create.error)) return { status: "hidden" }
  if (me.isError || create.isError) return { status: "unavailable", retry }
  if (!me.data?.share) return { status: "loading" }
  return { status: "ready", me: me.data, share: me.data.share }
}
